import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Search from '../search/Search';
import { getReturnVouchers, getReturnVoucher } from '../vouchersActions';
import { shipmentType, currentState } from '../utils/bindings';
import EnhancedTableHead from './EnhancedTableHead';
import VouchersLoader from './VouchersLoader';

const CustomLink = styled.a`
  color: #0000ee;
  text-decoration: none;
`;

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3)
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: 'auto'
  }
});

const EnhancedTable = ({
  classes,
  returnVouchers,
  watchCurrentReturnVoucher,
  numberOfCommands,
  dispatchGetReturnVouchers,
  loadingVouchers,
  dispatchGetReturnVoucher
}) => {
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [rowsPerPage] = useState(8);
  useEffect(() => {
    const params = {
      firstResult: page * rowsPerPage,
      maxResults: rowsPerPage,
      query
    };
    dispatchGetReturnVouchers(params);
  }, [
    page,
    dispatchGetReturnVouchers,
    rowsPerPage,
    watchCurrentReturnVoucher,
    query
  ]);

  const handleChangePage = (event, currentPage) => {
    setPage(currentPage);
  };
  return (
    <div>
      <Search setQuery={setQuery} setPage={setPage} query={query} />
      <Paper className={classes.root}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <EnhancedTableHead
              rowCount={numberOfCommands}
              loading={loadingVouchers}
            />
            {loadingVouchers ? (
              <VouchersLoader />
            ) : (
              <TableBody>
                {returnVouchers.map(returnVoucher => {
                  let trackingNumber = null;

                  if (returnVoucher.returnCommand) {
                    if (returnVoucher.returnCommand.trackingUrl) {
                      trackingNumber = (
                        <CustomLink
                          href={returnVoucher.returnCommand.trackingUrl}
                          target="_blank"
                        >
                          {returnVoucher.returnCommand.trackingNumber}
                        </CustomLink>
                      );
                    } else {
                      ({ trackingNumber } = returnVoucher.returnCommand);
                    }
                  } else {
                    trackingNumber = '-';
                  }

                  return (
                    <TableRow
                      hover
                      style={{ cursor: 'pointer' }}
                      role="checkbox"
                      tabIndex={-1}
                      key={returnVoucher.id}
                    >
                      <TableCell
                        align="left"
                        onClick={() =>
                          dispatchGetReturnVoucher(returnVoucher.id)
                        }
                      >
                        {returnVoucher.command.externalReference}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() =>
                          dispatchGetReturnVoucher(returnVoucher.id)
                        }
                      >
                        {moment(returnVoucher.date).format('DD/MM/YYYY')}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() =>
                          dispatchGetReturnVoucher(returnVoucher.id)
                        }
                      >
                        {`${returnVoucher.command.recipientFirstname} ${
                          returnVoucher.command.recipientLastname
                        }`}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() =>
                          dispatchGetReturnVoucher(returnVoucher.id)
                        }
                      >
                        {currentState[returnVoucher.state]}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() =>
                          dispatchGetReturnVoucher(returnVoucher.id)
                        }
                      >
                        {shipmentType[returnVoucher.shipmentType]}
                      </TableCell>
                      <TableCell align="left">{trackingNumber}</TableCell>
                      {[
                        ...new Set(
                          returnVoucher.items.map(item => item.returnNatureCode)
                        )
                      ].length === 2 ? (
                        <TableCell
                          align="left"
                          onClick={() =>
                            dispatchGetReturnVoucher(returnVoucher.id)
                          }
                        >
                          Échange et Retour
                        </TableCell>
                      ) : (
                        <TableCell
                          align="left"
                          onClick={() =>
                            dispatchGetReturnVoucher(returnVoucher.id)
                          }
                        >
                          {[
                            ...new Set(
                              returnVoucher.items.map(
                                item => item.returnNatureCode
                              )
                            )
                          ][0] === 'exchange'
                            ? 'Échange'
                            : 'Retour'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </div>
        <TablePagination
          disabled
          rowsPerPageOptions={[10]}
          component="div"
          count={numberOfCommands || 10}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            disabled: loadingVouchers || page < 1,
            'aria-label': 'Previous Page'
          }}
          nextIconButtonProps={{
            disabled:
              loadingVouchers || page > numberOfCommands / rowsPerPage - 1,
            'aria-label': 'Next Page'
          }}
          onChangePage={handleChangePage}
        />
      </Paper>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchGetReturnVouchers: params => dispatch(getReturnVouchers(params)),
  dispatchGetReturnVoucher: commandId => dispatch(getReturnVoucher(commandId))
});

const mapStateToProps = state => ({
  returnVouchers: state.vouchers.returnVouchers,
  numberOfCommands: state.vouchers.totalResults,
  loadingVouchers: state.vouchers.loadingVouchers,
  watchCurrentReturnVoucher: state.vouchers.watchCurrentReturnVoucher
});

EnhancedTable.propTypes = {
  classes: PropTypes.object.isRequired,
  returnVouchers: PropTypes.array.isRequired,
  watchCurrentReturnVoucher: PropTypes.bool.isRequired,
  numberOfCommands: PropTypes.number,
  dispatchGetReturnVouchers: PropTypes.func.isRequired,
  loadingVouchers: PropTypes.bool.isRequired,
  dispatchGetReturnVoucher: PropTypes.func
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(EnhancedTable));
